import type { BrandChecksWithApi, BrandHealth } from "@/types/api";

/**
 * PORTFEL MAREK (brand portfolio) tab — DNS/cert/NS/MX/SPF/DKIM/DMARC status
 * per x2mail-family brand. Data from GET /v1/vigil/brands (FROZEN
 * brand_health contract, 2026-07-11).
 *
 * Colouring:
 *   overall (UPPERCASE):  OK -> green | DEGRADED -> yellow | DOWN -> red | PENDING_NS -> grey
 *   checks (lowercase):   operational -> green | degraded -> yellow | major_outage -> red | no_data -> grey
 *
 * FAIL-LOUD: brands=[] is an explicit "no data" state, never hidden.
 * Ported from products/x2mail-admin/src/views/BrandsView.tsx.
 */
export interface BrandsViewProps {
  brands: BrandHealth[];
  loading: boolean;
  error?: string | null;
}

// overall UPPERCASE -> visual config
const OVERALL_CFG: Record<string, { label: string; cardClass: string; badgeClass: string }> = {
  OK: {
    label: "OK",
    cardClass: "border-green-800 bg-green-950/20",
    badgeClass: "bg-green-900/50 text-green-300",
  },
  DEGRADED: {
    label: "DEGRADED",
    cardClass: "border-yellow-800 bg-yellow-950/20",
    badgeClass: "bg-yellow-900/50 text-yellow-300",
  },
  DOWN: {
    label: "DOWN",
    cardClass: "border-red-800 bg-red-950/30",
    badgeClass: "bg-red-900/50 text-red-300",
  },
  PENDING_NS: {
    label: "PENDING NS",
    cardClass: "border-zinc-700 bg-zinc-900",
    badgeClass: "bg-zinc-800 text-zinc-400",
  },
};

// checks lowercase -> visual config
const CHECK_CFG: Record<string, { label: string; badgeClass: string }> = {
  operational: {
    label: "ok",
    badgeClass: "bg-green-900/50 text-green-400",
  },
  // Distinct from "operational": this domain is deliberately redirect-only (e.g. voicetomail.eu
  // -> word2mail.com per ADR-0346) and status_page_probe.py confirmed the 301 lands on the
  // right canonical target. Rendered green (healthy) but labeled differently so an operator can
  // still tell "real front" apart from "intentional redirect" at a glance.
  redirect_ok: {
    label: "redirect",
    badgeClass: "bg-green-900/50 text-green-400",
  },
  degraded: {
    label: "degraded",
    badgeClass: "bg-yellow-900/50 text-yellow-400",
  },
  major_outage: {
    label: "outage",
    badgeClass: "bg-red-900/50 text-red-400",
  },
  no_data: {
    label: "brak",
    badgeClass: "bg-zinc-800 text-zinc-500",
  },
};

const CHECK_ORDER: (keyof BrandChecksWithApi)[] = [
  "http",
  "cert",
  "ns",
  "mx",
  "spf",
  "dkim",
  "dmarc",
  "api",
];

// Human-readable description per check, shown as a hover tooltip next to the
// compact abbreviation badge. Most checks are self-explanatory to an admin
// (DNS/mail jargon); "api" specifically needs the long form — it's the
// silent-failure-mode check (HTTP 200, page looks healthy, bundle calls the
// wrong backend) added 2026-07-25 after the dyktuj2mail prod-promotion
// incident, so a bare "api" abbreviation would not communicate what broke.
const CHECK_LABELS: Record<keyof BrandChecksWithApi, string> = {
  http: "Front marki odpowiada po HTTPS",
  cert: "Certyfikat TLS",
  ns: "Delegacja DNS (NS)",
  mx: "Poczta — rekord MX",
  spf: "Poczta — SPF",
  dkim: "Poczta — DKIM",
  dmarc: "Poczta — DMARC",
  api: "Front marki wskazuje na produkcyjne API, nie na dev — cichy tryb awarii: strona wygląda zdrowo (HTTP 200), ale woła zły backend",
};

export function BrandsView({ brands, loading, error }: BrandsViewProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-zinc-500">Ładowanie danych marek…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-red-700 bg-red-950 px-4 py-3 text-sm text-red-400"
      >
        Błąd pobierania portfela marek: {error}
      </div>
    );
  }

  if (brands.length === 0) {
    return (
      <div
        role="status"
        className="rounded-lg border border-zinc-700 bg-zinc-900 px-6 py-12 text-center"
      >
        <p className="text-sm font-semibold text-zinc-400">PORTFEL MAREK — brak danych</p>
        <p className="mt-1 text-xs text-zinc-600">
          Producent <span className="font-mono">status_page_probe</span> nie zapisał jeszcze żadnych
          danych do Firestore <span className="font-mono">brand_health/latest</span>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-zinc-200">Portfel marek</h2>
      {brands.map((brand) => (
        <BrandCard key={brand.brandId} brand={brand} />
      ))}
    </div>
  );
}

interface BrandCardProps {
  brand: BrandHealth;
}

function BrandCard({ brand }: BrandCardProps) {
  // Fallback to PENDING_NS visual if an unknown overall value arrives from the backend.
  const overall = OVERALL_CFG[brand.overall] ?? OVERALL_CFG["PENDING_NS"];
  // brand.checks is BrandChecks (generated, no "api" field yet) —
  // BrandChecksWithApi's "api" is optional, so a value without it (current
  // wire shape, and any pre-2026-07-25 Firestore doc) is still assignable
  // here; `?? "no_data"` below is what makes a missing key render as honest
  // "brak", never a crash or fake-green.
  const checks: BrandChecksWithApi = brand.checks;

  return (
    <div className={["rounded-lg border p-4", overall.cardClass].join(" ")}>
      {/* Brand header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-zinc-100">{brand.label}</p>
          <p className="text-xs text-zinc-500">{brand.domain}</p>
        </div>
        <div className="flex items-center gap-2">
          {brand.isPendingNs && (
            <span className="rounded bg-zinc-700 px-2 py-0.5 text-xs font-medium text-zinc-300">
              PENDING NS
            </span>
          )}
          <span
            className={["rounded px-2 py-0.5 text-xs font-semibold", overall.badgeClass].join(" ")}
          >
            {overall.label}
          </span>
        </div>
      </div>

      {/* Per-check status row */}
      <div className="mt-3 flex flex-wrap gap-2">
        {CHECK_ORDER.map((key) => {
          const val = checks[key] ?? "no_data";
          const cfg = CHECK_CFG[val] ?? CHECK_CFG["no_data"];
          return (
            <div key={key} className="flex items-center gap-1">
              <span title={CHECK_LABELS[key]} className="text-xs uppercase text-zinc-600">
                {key}
              </span>
              <span className={["rounded px-1.5 py-0.5 text-xs", cfg.badgeClass].join(" ")}>
                {cfg.label}
              </span>
            </div>
          );
        })}
      </div>

      <p className="mt-2 text-xs text-zinc-700">Aktualizacja: {brand.updatedAt}</p>
    </div>
  );
}
